import { Helmet, HelmetProvider } from 'react-helmet-async';
import { ChartBar, ArrowsClockwise } from '@phosphor-icons/react';
import { Button } from 'components/ui/button';
import { useAcquisitionStatistics } from './hooks/useAcquisitionStatistics';
import {
  FinancialsCard,
  KpiCards,
  PhaseFunnelChart,
  RegionDistributionChart,
  RegionPerformanceTable,
  ResponsibleDistributionChart,
  ResponsiblePerformanceTable,
  SourceDistributionChart,
  YearlyTrendChart,
  YearlyYieldChart,
} from './components';

const AcquisitionStatisticsPage = () => {
  const { data, isLoading, refresh } = useAcquisitionStatistics();

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Acquisitie Statistieken &mdash; Yally</title>
        </Helmet>
      </HelmetProvider>

      <div className="flex flex-col gap-6 pb-10">
        {/* Header */}
        <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-moss-primary/10 p-2">
              <ChartBar className="h-6 w-6 text-moss-primary" weight="duotone" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-neutral-primary">
                Acquisitie statistieken
              </h1>
              <p className="text-sm text-neutral-secondary-mid">
                Performance van het acquisitieteam, instroom van dossiers, yield en
                transactievolume
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={isLoading}
            className="self-start md:self-auto"
          >
            <ArrowsClockwise
              className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
              weight="duotone"
            />
            Verversen
          </Button>
        </header>

        {/* KPI row */}
        <KpiCards overview={data?.overview} loading={isLoading} />

        {/* Pipeline + Source side-by-side */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <PhaseFunnelChart data={data?.byPhase} loading={isLoading} />
          </div>
          <div className="lg:col-span-1">
            <SourceDistributionChart data={data?.bySource} loading={isLoading} />
          </div>
        </section>

        {/* Region + Responsible (charts) */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <RegionDistributionChart data={data?.byRegion} loading={isLoading} />
          <ResponsibleDistributionChart
            data={data?.byResponsible}
            loading={isLoading}
          />
        </section>

        {/* Per-provincie performance table — full width */}
        <section>
          <RegionPerformanceTable data={data?.byRegion} loading={isLoading} />
        </section>

        {/* Yearly trend + yield evolution side-by-side */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <YearlyTrendChart data={data?.byYear} loading={isLoading} />
          <YearlyYieldChart data={data?.byYear} loading={isLoading} />
        </section>

        {/* Financials + Responsible performance table */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <FinancialsCard data={data?.financials} loading={isLoading} />
          <ResponsiblePerformanceTable
            data={data?.byResponsible}
            loading={isLoading}
          />
        </section>
      </div>
    </>
  );
};

export default AcquisitionStatisticsPage;
